import { Resource } from "dtos/stage.dto";
import { Deadline } from "pages/TaskTemplate/deadline";
import { T_MongoId, Types } from "types/_utils.types";

// ─── Enums ────────────────────────────────────────────────────────────────────

export enum MemberRole {
  OWNER = "owner",
  VERIFIER = "verifier",
  ACTOR = "actor",
}

export enum MemberKind {
  EXTERNAL = "external",
  INTERNAL = "internal",
}

export enum MemberTarget {
  ROLE = "role",
  INDIVIDUAL = "individual",
}

export enum ActivationCriteriaCategory {
  PARENT_TASK_ACTIVE = "parent_task_active",
  PREVIOUS_TASK_COMPLETE = "previous_task_complete",
  OUTCOME_ACHIEVED = "outcome_achieved",
}

export enum CompletionCriteriaCategory {
  SUB_TASKS_COMPLETED = "all_subtask_complete",
  ACTION_BY_MEMBER = "member_caused_action",
}

// ─── Task Member ──────────────────────────────────────────────────────────────

export type TaskItemMember = {
  role: MemberRole;
  kind: MemberKind;
  target: MemberTarget;
  assigneeRole?: string;
  assigneeId?: T_MongoId;
};

// ─── Criteria ─────────────────────────────────────────────────────────────────

export type ActivationCriteria =
  | { category: ActivationCriteriaCategory.PARENT_TASK_ACTIVE | ActivationCriteriaCategory.PREVIOUS_TASK_COMPLETE }
  | { category: ActivationCriteriaCategory.OUTCOME_ACHIEVED; task: T_MongoId; outcome: T_MongoId };

export type CompletionCriteria =
  | { category: CompletionCriteriaCategory.SUB_TASKS_COMPLETED }
  | { category: CompletionCriteriaCategory.ACTION_BY_MEMBER; member: MemberRole; outcome: T_MongoId };

export type NestedCriteria<T extends { category: string }> = {
  minimumRequired: number;
  options: Array<NestedCriteria<T> | T>;
};

// ─── Task Item ────────────────────────────────────────────────────────────────

/** A single task item as returned by the view endpoint (structured) */
export type StructuredTaskItemDto = {
  _id: T_MongoId;
  code: string;
  name: string;
  description?: string;
  members: TaskItemMember[];
  relatedStage?: T_MongoId;
  activationCriteria?: NestedCriteria<ActivationCriteria>;
  completionCriteria?: NestedCriteria<CompletionCriteria>;
  deadline?: { days: number; businessDays?: boolean };
  activatedAt?: Date;
  dueAt?: Date;
  meta: Record<string, unknown>;
  /** Position in the sibling chain (0-indexed) */
  order: number;
  parent: T_MongoId;
  /** Nested subtasks (max 1 level deep) */
  subtasks: StructuredTaskItemDto[];
};

// ─── Task Group ───────────────────────────────────────────────────────────────

/** A task group as returned by the view endpoint */
export type StructuredTaskGroupDto = {
  _id: T_MongoId;
  name: string;
  code: string;
  meta: Record<string, unknown>;
  tasks: StructuredTaskItemDto[];
};

// ─── Task Board Template ──────────────────────────────────────────────────────

/** Full template as returned by GET /:templateId */
export type TaskBoardTemplateViewDto = {
  _id: T_MongoId;
  name: string;
  resource: Resource;
  autoStart: boolean;
  organisation: T_MongoId;
  createdBy: T_MongoId;
  groups: StructuredTaskGroupDto[];
  createdAt: string;
  updatedAt: string;
};

/** List item shape — tasks are excluded on the list endpoint */
export type TaskBoardTemplateListItemDto = Omit<TaskBoardTemplateViewDto, "groups"> & {
  groups: Omit<StructuredTaskGroupDto, "tasks">[];
};

// ─── Payloads ─────────────────────────────────────────────────────────────────

export type CreateTaskBoardTemplatePayload = {
  name: string;
  resource: Resource;
  autoStart: boolean;
  organisation: T_MongoId;
};

export type AddTaskGroupPayload = {
  name: string;
  /** 3 uppercase alpha chars — optional, auto-generated if omitted */
  code?: string;
  meta?: Record<string, unknown>;
};

export type AddTaskItemPayload = {
  name: string;
  description?: string;
  relatedStage?: T_MongoId;
  /** null = root task; ObjectId = subtask under this parent */
  parent: T_MongoId | null;
  /** null = first in chain; ObjectId = comes after this task */
  previous: T_MongoId | null;
  meta?: Record<string, unknown>;
};

export type TaskItemUpdatePayload = {
  name?: string;
  description?: string | null;
  relatedStage?: Types.ObjectId | null;
  deadline?: Deadline | null;
  meta?: Record<string, unknown> | null;
};
